import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import FetchModule from '../../Model/Share/Network/FetchModule';
import { callingCodeToCountry } from '../../Utils/phoneUtils';

import TotalModalWrapper from '../../Model/Share/Normal/TotalModalWrapper';
import CountryCodeSelectBox from '../../Component/Common/CountryCodeSelectBox';

import { ReactComponent as AiIcon } from '../../image/Ai2.svg';
import { ReactComponent as MemberIcon } from '../../image/Member.svg';
import { ReactComponent as ClassIcon } from '../../image/Class2.svg';
import { ReactComponent as ChartIcon } from '../../image/Chart.svg';
import { ReactComponent as LockerIcon } from '../../image/Locker.svg';
import { ReactComponent as BoardIcon } from '../../image/Board.svg';
import { ReactComponent as NoteIcon } from '../../image/Note.svg';
import { ReactComponent as NoticeIcon } from '../../image/Notice.svg';
import { ReactComponent as SettingIcon } from '../../image/Setting.svg';

import { ReactComponent as ViewOnIcon } from '../../image/ViewOn.svg';
import { ReactComponent as ViewOffIcon } from '../../image/ViewOff.svg';

const PERMISSION_KEYS = ['ai', 'member', 'class', 'stats', 'locker', 'board', 'note', 'notice', 'setting'];
const FIXED_PERMISSION_KEYS = ['member', 'board'];

const PERMISSION_ICONS = {
  ai: AiIcon,
  member: MemberIcon,
  class: ClassIcon,
  stats: ChartIcon,
  locker: LockerIcon,
  board: BoardIcon,
  note: NoteIcon,
  notice: NoticeIcon,
  setting: SettingIcon
};

export default function SubAccountModal(props) {
  const { isNew, initialData, onClickExit, onSuccess } = props;

  const history = useHistory();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const initial = initialData || {};

  const [name, setName] = useState(initial.name || '');
  const [phoneInput, setPhoneInput] = useState(initial.phone || '');
  const [managerId, setManagerId] = useState(initial.loginId || '');
  const [password, setPassword] = useState('');
  const [isViewPassword, setIsViewPassword] = useState(false);
  const [permissions, setPermissions] = useState(new Set([
    ...(initial.permissions || []).map(key => key.toLowerCase()),
    ...FIXED_PERMISSION_KEYS
  ]));
  const [isIdChecked, setIsIdChecked] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({ countryCode: initial.countryCode || '+82' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  function onClickIdCheck() {
    if (!managerId || managerId === '') {
      alert(t('myStoreSetting.account.sub.alert.idRequired'));
      return;
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('sub-account/check', 'POST', { account: managerId });

        if (responseData.status === 200) {
          if (responseData.data?.exists) {
            setIsIdChecked(false);
            alert(t('myStoreSetting.account.sub.alert.idDuplicated'));
          }
          else {
            setIsIdChecked(true);
          }
        }
        else if (responseData.status === 401) history.push('/');
        else alert(t('myStoreSetting.alert.serverError'));
      }
    )();
  }

  function togglePermission(key) {
    if (FIXED_PERMISSION_KEYS.includes(key)) return;

    const next = new Set(permissions);

    if (next.has(key)) next.delete(key);
    else next.add(key);

    setPermissions(next);
  }

  function onClickAddButton() {
    if (isSubmitting) return;
    if (!name || name === '') {
      alert(t('myStoreSetting.account.sub.alert.nameRequired'));
      return;
    }
    if (!phoneInput || phoneInput === '') {
      alert(t('myStoreSetting.account.sub.alert.phoneRequired'));
      return;
    }
    if (!managerId || managerId === '') {
      alert(t('myStoreSetting.account.sub.alert.idRequired'));
      return;
    }
    if (!isIdChecked) {
      alert(t('myStoreSetting.account.sub.alert.idCheckRequired'));
      return;
    }
    if (!password || password === '') {
      alert(t('myStoreSetting.account.sub.alert.passwordRequired'));
      return;
    }

    const requestData = {
      account: managerId,
      password: password,
      name: name,
      phone: phoneInput,
      countryCode: selectedCountry.countryCode,
      countryIso: callingCodeToCountry[selectedCountry.countryCode] || 'KR',
      role: Array.from(permissions).map(key => key.toUpperCase())
    };

    setIsSubmitting(true);

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('sub-account/save', 'POST', requestData);

        setIsSubmitting(false);

        if (responseData.status === 200) {
          if (onSuccess) onSuccess();
          onClickExit();
        }
        else if (responseData.status === 201) {
          setIsIdChecked(false);
          alert(t('myStoreSetting.account.sub.alert.idDuplicated'));
        }
        else if (responseData.status === 401) history.push('/');
        else alert(t('myStoreSetting.alert.serverError'));
      }
    )();
  }

  function onClickEditButton() {
    if (isSubmitting) return;
    if (!initial.id) return;
    if (!name || name === '') {
      alert(t('myStoreSetting.account.sub.alert.nameRequired'));
      return;
    }
    if (!phoneInput || phoneInput === '') {
      alert(t('myStoreSetting.account.sub.alert.phoneRequired'));
      return;
    }

    // role 배열은 전체(있는 권한 + 빠진 권한 회수) 의미. 현재 토글된 권한만 보내면 백엔드가 나머지는 false 처리 //
    const requestData = {
      id: initial.id,
      name: name,
      phone: phoneInput,
      countryCode: selectedCountry.countryCode,
      countryIso: callingCodeToCountry[selectedCountry.countryCode] || 'KR',
      role: Array.from(permissions).map(key => key.toUpperCase())
    };

    setIsSubmitting(true);
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('sub-account/modify', 'POST', requestData);

        setIsSubmitting(false);

        if (responseData.status === 200) {
          if (onSuccess) onSuccess();
          onClickExit();
        }
        else if (responseData.status === 401) history.push('/');
        else if (responseData.status === 404) alert(t('myStoreSetting.account.sub.alert.notFound'));
        else alert(t('myStoreSetting.alert.serverError'));
      }
    )();
  }

  // 관리자 ID 입력 영역 - 생성 모드만 편집 가능, 편집 모드는 readOnly //
  function renderManagerIdInput() {
    if (!isNew) {
      return (
        <InputBox borderColor={theme.line} backgroundColor={theme.subBackground}>
          <InputField
            color={theme.subText}
            placeholderColor={theme.subText}
            type='text'
            value={initial.loginId || ''}
            readOnly />
        </InputBox>
      );
    }

    return (
      <IdRow>
        <InputBox borderColor={theme.line} backgroundColor={theme.subBackground}>
          <InputField
            color={theme.text}
            placeholderColor={theme.subText}
            type='text'
            value={managerId}
            onChange={(e) => {
              setManagerId(e.target.value);
              setIsIdChecked(false);
            }} />
        </InputBox>
        <IdCheckButton backgroundColor={theme.primary} onClick={onClickIdCheck}>
          <ButtonText color={theme.subBackground}>{t('myStoreSetting.account.sub.form.idCheck')}</ButtonText>
        </IdCheckButton>
      </IdRow>
    );
  }

  // 비밀번호 입력 영역 - 생성 모드만 편집 가능, 편집 모드는 마스킹 readOnly //
  function renderPasswordInput() {
    if (!isNew) {
      return (
        <InputField
          color={theme.subText}
          placeholderColor={theme.subText}
          type='password'
          value='********'
          readOnly />
      );
    }

    return (
      <>
        <InputField
          color={theme.text}
          placeholderColor={theme.subText}
          type={isViewPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)} />
        {
          isViewPassword ?
            <ViewOffEye color={theme.line} onClick={() => setIsViewPassword(!isViewPassword)} />
            :
            <ViewOnEye color={theme.line} onClick={() => setIsViewPassword(!isViewPassword)} />
        }
      </>
    );
  }

  // 권한 토글 1개 //
  function renderPermissionToggle(key) {
    const isOn = permissions.has(key);
    const isFixed = FIXED_PERMISSION_KEYS.includes(key);
    const IconComponent = PERMISSION_ICONS[key];

    return (
      <PermissionToggle
        key={'SubAccountPermissionToggle-' + key}
        isOn={isOn}
        isFixed={isFixed}
        onBackground={theme.primary}
        offBackground={theme.subBackground}
        onBorder={theme.primary}
        offBorder={theme.line}
        onClick={() => togglePermission(key)}>
        <ToggleIconWrapper>
          <StyledToggleIcon
            as={IconComponent}
            color={isOn ? theme.background : theme.line}
            detailColor={isOn ? theme.primary : theme.subBackground} />
        </ToggleIconWrapper>
        <ToggleText color={isOn ? theme.background : theme.line}>
          {t('myStoreSetting.account.sub.permission.' + key)}
        </ToggleText>
      </PermissionToggle>
    );
  }

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <TitleColumn>
          <Title color={theme.text}>{t(isNew ? 'myStoreSetting.account.sub.form.addTitle' : 'myStoreSetting.account.sub.form.editTitle')}</Title>
          <Description color={theme.gray}>{t(isNew ? 'myStoreSetting.account.sub.form.addDescription' : 'myStoreSetting.account.sub.form.editDescription')}</Description>
        </TitleColumn>

        <FormGrid>
          <FieldColumn>
            <FieldLabel color={theme.text}>{t('myStoreSetting.account.sub.form.managerName')}</FieldLabel>
            <InputBox borderColor={theme.line} backgroundColor={theme.subBackground}>
              <InputField
                color={theme.text}
                placeholderColor={theme.subText}
                type='text'
                value={name}
                onChange={(e) => setName(e.target.value)} />
            </InputBox>
          </FieldColumn>
          <FieldColumn>
            <FieldLabel color={theme.text}>{t('myStoreSetting.account.sub.form.phone')}</FieldLabel>
            <PhoneRow>
              <CountryCodeSelectBox
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry} />
              <InputBox borderColor={theme.line} backgroundColor={theme.subBackground}>
                <InputField
                  color={theme.text}
                  placeholderColor={theme.subText}
                  type='tel'
                  inputMode='numeric'
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value.replace(/[^0-9]/g, ''))} />
              </InputBox>
            </PhoneRow>
          </FieldColumn>
          <FieldColumn>
            <FieldLabel color={theme.text}>{t('myStoreSetting.account.sub.form.managerId')}</FieldLabel>
            {renderManagerIdInput()}
            <IdAvailableRow isVisible={isNew && isIdChecked} aria-hidden={!isNew}>
              <IdAvailableCheck color={theme.primary}>✓</IdAvailableCheck>
              <IdAvailableText color={theme.primary}>{t('myStoreSetting.account.sub.form.idAvailable')}</IdAvailableText>
            </IdAvailableRow>
          </FieldColumn>
          <FieldColumn>
            <FieldLabel color={theme.text}>{t('myStoreSetting.account.sub.form.password')}</FieldLabel>
            <InputBox borderColor={theme.line} backgroundColor={theme.subBackground}>
              {renderPasswordInput()}
            </InputBox>
            <IdAvailableRow isVisible={false} aria-hidden>
              <IdAvailableCheck>✓</IdAvailableCheck>
              <IdAvailableText>{t('myStoreSetting.account.sub.form.idAvailable')}</IdAvailableText>
            </IdAvailableRow>
          </FieldColumn>
        </FormGrid>

        <PermissionGrid>
          {PERMISSION_KEYS.map(key => renderPermissionToggle(key))}
        </PermissionGrid>
        
        <ButtonRow>
          <CloseButton backgroundColor={theme.buttonFill} onClick={onClickExit}>
            <ButtonText color={theme.text}>{t('myStoreSetting.account.sub.form.close')}</ButtonText>
          </CloseButton>
          <SubmitButton backgroundColor={theme.primary} onClick={isNew ? onClickAddButton : onClickEditButton}>
            <ButtonText fontFamily='NotoSansKR-Bold' color={theme.subBackground}>{t(isNew ? 'myStoreSetting.account.sub.form.submitAdd' : 'myStoreSetting.account.sub.form.submitEdit')}</ButtonText>
          </SubmitButton>
        </ButtonRow>
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 530px;
  padding: 20px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};
`;

const TitleColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 4px;

  width: 100%;

  padding-bottom: 10px;
`;

const Title = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const Description = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;
  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const FormGrid = styled.div`
  display: grid;

  grid-template-columns: 1fr 1fr;

  column-gap: 20px;
  row-gap: 20px;

  width: 100%;
`;

const FieldColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 5px;

  width: 100%;
  min-width: 0;
`;

const FieldLabel = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const InputBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 8px;

  flex: 1 1 0;
  min-width: 0;

  width: 100%;
  height: 32px;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};
`;

const InputField = styled.input`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  outline: none;
  border: 0px;

  flex: 1 0 0;
  min-width: 0;
  height: 32px;

  color: ${props => props.color};
  background-color: transparent;

  ::placeholder {
    color: ${props => props.placeholderColor};
  }
`;

const PhoneRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;

  width: 100%;
`;

const IdRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;

  width: 100%;
`;

const IdCheckButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  height: 32px;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const IdAvailableRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;

  width: 100%;

  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
`;

const IdAvailableCheck = styled.span`
  font-family: NotoSansKR-Bold;
  font-size: 12px;

  color: ${props => props.color};

  line-height: 1;
`;

const IdAvailableText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const ViewOnEye = styled(ViewOnIcon)`
  width: 20px;
  height: auto;

  flex-shrink: 0;

  path {
    stroke: ${props => props.color};
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ViewOffEye = styled(ViewOffIcon)`
  width: 20px;
  height: auto;

  flex-shrink: 0;

  path {
    fill: ${props => props.color};
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const PermissionGrid = styled.div`
  display: grid;

  grid-template-columns: repeat(3, 1fr);

  column-gap: 15px;
  row-gap: 10px;

  width: 100%;

  padding: 20px 0;
`;

const PermissionToggle = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 5px;

  width: 100%;
  height: 32px;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.isOn ? props.onBorder : props.offBorder};
  background-color: ${props => props.isOn ? props.onBackground : props.offBackground};

  transition: all 0.15s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.isFixed ? 'default' : 'pointer'};
    }
  }
`;

const ToggleIconWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 16px;
  height: 16px;
`;

const StyledToggleIcon = styled.svg`
  width: 16px;
  height: 16px;

  & path {
    fill: ${props => props.color};
  }

  & path[fill='#1d1d1d' i] {
    fill: ${props => props.detailColor};
  }
`;

const ToggleText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 12px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 10px;

  width: 100%;

  padding-top: 20px;
`;

const CloseButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 90px;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SubmitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1 0 0;
  height: 40px;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ButtonText = styled.p`
  font-family: ${props => props.fontFamily || 'NotoSansKR-Medium'};
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;
